import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {useUser} from '@dofleini/security';
import {proposalrequest} from '@mep/pae-commons';
import {Divider, Form, Input, message, Select} from 'antd';
import compact from 'lodash/compact';
import findIndex from 'lodash/findIndex';
import compose from 'lodash/fp/compose';
import uniqByFP from 'lodash/fp/uniqBy';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import RequiredText from '@/components/RequiredText/RequiredText';
import {useDialogsContext} from '@/contexts/DialogsContext';
import ProposalRequestPartnerGeneralDataModal
  from '@/modules/proposalRequest/containers/ProposalRequestPartnerGeneralDataModal';
import {DIALOG_GENERAL_PARTNER_NAMESPACE} from '@/modules/proposalRequest/contants/proposalRequest';
import {useProposalCreation} from '@/modules/proposalRequest/contexts/ProposalRequestCreation';
import {getUserFullName} from '@/utils/userFullName';

const {Option} = Select;

const {STATE_MIPYME, ORGANIZATION_PMS} = proposalrequest;

const ProposalPartnersAdministration = ({
  value,
  id,
  defaultValue,
  actionText,
  onChange,
  canAdd,
  canAddRepresentative,
  dialogContext,
  isInspection,
  addRepresentativeText,
  externalData,
  ...rest
}) => {
  const {openDialog} = useDialogsContext(dialogContext || DIALOG_GENERAL_PARTNER_NAMESPACE);
  const {t} = useTranslation('proposalRequest');
  const {proposal} = useProposalCreation();
  const {user} = useUser();
  const isStateMipyme = useMemo(() => [STATE_MIPYME, ORGANIZATION_PMS].includes((externalData || proposal)?.subjectType), [externalData, proposal]);

  let [data, setData] = useState([]);
  let [stateData, setStateData] = useState([]);
  const [title, setTitle] = useState('');

  const membersList = useMemo(() => value?.members || [], [value?.members]);

  useEffect(() => {
    const arrayToSearch = [...(externalData || proposal)?.partners?.map(e => e) || '', ...membersList];
    if (canAddRepresentative) {
      arrayToSearch.push(value?.representative);
    }
    const copy = compact(compose(uniqByFP('idNumber'))(arrayToSearch)) || [];
    const statePartnersRepresentatives = compact(compose(uniqByFP('idNumber'))([...(externalData || proposal)?.statePartners?.map(({representative}) => representative.isRepresentative.active ? representative : value?.representative) || '', ...membersList]));
    setData(copy);
    setStateData(statePartnersRepresentatives);
  }, [canAddRepresentative, externalData, membersList, proposal, proposal.partners, proposal.statePartners, value?.representative]);

  const selectedOptions = useMemo(() => {
    return compact([...(value?.representative ? [value?.representative] : []), ...(membersList || [])]);
  }, [value?.representative, membersList]);

  const onAddRequestModal = useCallback((index, isRepresentative) => {
    const newTitle = isRepresentative ? 'proposalRequest.form.organizations.administration.addRepresentativeText' : 'proposalRequest.form.organizations.administration.addText';
    setTitle(t(newTitle));
    openDialog({recordIndex: index, isRepresentative});
  }, [openDialog, t]);

  const onEditRequestPartner = useCallback(async (record, i) => {
    setTitle(t('proposalRequest.form.organizations.administration.editMember'));
    // civilStatusInfo: is to reuse a existen component
    openDialog({partner: {'civilStatusInfo': {...record}}, isEdit: true, recordIndex: i});
  }, [openDialog, t]);

  const onEditRequestRepresentative = useCallback(async (record, i) => {
    // civilStatusInfo: is to reuse a existen component
    setTitle(t('proposalRequest.form.organizations.administration.editRepresentative'));
    openDialog({partner: {'civilStatusInfo': {...record}}, isRepresentative: true, isEdit: true, recordIndex: i});
  }, [openDialog, t]);

  const onSetRepresentative = useCallback(async (dataValues) => {
    return new Promise((resolve, reject) => {
      // verify the members by idNumber
      const exist = membersList?.some(e => e?.idNumber === dataValues?.idNumber);
      const existOnData = data.some(e => e?.idNumber === dataValues?.idNumber);
      if (!exist && !existOnData) {
        isStateMipyme && isInspection ? stateData.push(dataValues) : data.push(dataValues);
        isStateMipyme && isInspection ? setStateData([...stateData]) : setData([...data]);
        onChange({...value, representative: dataValues});
      } else {
        message.error(t('proposalRequest.form.organizations.inspectionControl.repeated'));
        reject();
      }
      resolve(true);
    });
  }, [data, isInspection, isStateMipyme, membersList, onChange, stateData, t, value]);

  const onAddPartner = useCallback(async (partner, isEdit, recordIndex, isRepresentative) => {
    if (isRepresentative && !isEdit)
      return onSetRepresentative(partner, recordIndex);

    return new Promise((resolve, reject) => {
      const list = membersList;
      try {
        if (!isEdit) {
          // verify the members by idNumber
          const exist = list?.some(e => e?.idNumber === partner?.idNumber);
          const existOnData = data.some(e => e?.idNumber === partner?.idNumber);
          if (!exist && !existOnData) {
            isStateMipyme && isInspection ? stateData.push(partner) : data.push(partner);
            list[recordIndex] = {...partner};
            isStateMipyme && isInspection ? setStateData([...stateData]) : setData([...data]);
            onChange({...value, members: list});
            resolve(true);
          } else {
            message.error(t('proposalRequest.form.organizations.inspectionControl.repeated'));
            reject();
          }
        } else if (isEdit && !isRepresentative) {
          let elementOnDataIndex = isStateMipyme && isInspection ? stateData.findIndex(e => e?.idNumber === partner?.idNumber) : data.findIndex(e => e?.idNumber === partner?.idNumber);
          isStateMipyme && isInspection ? stateData[elementOnDataIndex] = {...partner} : data[elementOnDataIndex] = {...partner};
          list[recordIndex] = {...partner};
          isStateMipyme && isInspection ? setStateData([...data]) : setData([...data]);
          onChange({...value, members: list});
          resolve(true);
        } else {
          let elementOnDataIdx = isStateMipyme ? stateData.findIndex(e => e?.idNumber === partner?.idNumber) : data.findIndex(e => e?.idNumber === partner?.idNumber);
          isStateMipyme ? stateData[elementOnDataIdx] = {...partner} : data[elementOnDataIdx] = {...partner};
          isStateMipyme ? setStateData([...data]) : setData([...data]);
          onChange({...value, representative: {...partner}});
          resolve(true);
        }
      } catch (e) {
        reject(e);
      }
    });
  }, [data, isInspection, isStateMipyme, membersList, onChange, onSetRepresentative, stateData, t, value]);

  const processBeforeDelete = useCallback((record) => {
    const isPartner = proposal?.partners?.some(e => e?.idNumber === record?.idNumber);
    if (!isPartner) {
      const selectIndex = findIndex((isStateMipyme && isInspection) ? stateData : data, {idNumber: record?.idNumber});
      const wasDeleted = isStateMipyme && isInspection ? stateData?.splice(selectIndex, 1) : data?.splice(selectIndex, 1);
      if (wasDeleted)
        isStateMipyme && isInspection ? setStateData(stateData) : setData(data);
    }
  }, [data, isInspection, isStateMipyme, proposal?.partners, stateData]);

  const onRemoveAndUpdate = useCallback((index, remove) => {
    const record = membersList[index];
    processBeforeDelete(record);
    remove(index);
    onChange({...value, members: membersList.filter(e => e?.idNumber !== record?.idNumber)});
  }, [membersList, onChange, processBeforeDelete, value]);

  const onRemoveAndUpdateRepresentative = useCallback(() => {
    const record = value?.representative;
    processBeforeDelete(record);
    onChange({...value, representative: {}});
  }, [onChange, processBeforeDelete, value]);

  return (
    <>
      <ProposalRequestPartnerGeneralDataModal title={title} onConfirm={onAddPartner} dialogContext={dialogContext}/>
      <div className="ant-row ant-form-item w-full truncate mb-0">
        <RequiredText value={t('proposalRequest.form.organizations.inspectionControl.representative')} required={true}/>
      </div>
      <div className="flex items-center gap-x-2 w-full">
        <Form.Item
          name={[id, 'representative', 'idNumber']}
          rules={[{required: true}]}
          required
          className="w-full truncate">
          <Select
            showSearch={false}
            filterOption={false}
            allowClear={false}
            placeholder={t('selectPlaceholder')}
            className="w-full"
            size={'large'}
            required
            dropdownRender={menu => (
              <div>
                {menu}
                {canAddRepresentative && <>
                  <Divider className="my-1"/>
                  <div className="mx-3 py-1 min-h-6 cursor-pointer text-primary"
                    onClick={() => onAddRequestModal(0, true)}>
                    {t(addRepresentativeText || 'proposalRequest.form.organizations.administration.addText')}
                  </div>
                </>}
              </div>
            )}
          >
            {
              (isStateMipyme && isInspection ? stateData : data)?.map((opt, i) => (
                <Option
                  disabled={selectedOptions.find((el) => el?.idNumber === opt?.idNumber)}
                  key={i}
                  value={opt?.idNumber}
                >
                  {'(' + opt?.idNumber + ')' + ' ' + getUserFullName(opt)}
                </Option>
              ))
            }
          </Select>
        </Form.Item>
        {value?.representative && value?.representative?.firstName && <div
          onClick={() => onEditRequestRepresentative(value?.representative, 0)}
          className="text-center cursor-pointer flex justify-center hover:text-primary"
          style={{height: '40px', minWidth: '30px'}}>
          <EditOutlined/>
        </div>}
        {<div
          onClick={onRemoveAndUpdateRepresentative}
          className="text-center cursor-pointer flex justify-center hover:text-primary"
          style={{height: '40px', minWidth: '30px'}}>
          <DeleteOutlined/>
        </div>}
      </div>
      {!(user?.isSoleOwner && isInspection) &&
        <div>
          <div className="ant-row ant-form-item w-full truncate mb-0">
            <RequiredText value={t('proposalRequest.form.organizations.inspectionControl.members')}
              required={false}/>
          </div>
          <Form.List
            name={[id, 'members']}
            {...rest}
            initialValue={[defaultValue]}
          >
            {(fields, {add, remove}) => {
              return <>
                {fields.map((field, index) => {
                  return <Input.Group compact className="flex" key={index}>
                    <div className="flex items-center gap-x-2 w-full">
                      <Form.Item name={[field?.name, 'idNumber']} className="w-full truncate">
                        <Select
                          showSearch={false}
                          filterOption={false}
                          allowClear={false}
                          placeholder={t('selectPlaceholder')}
                          className="w-full"
                          size={'large'}
                          required
                          dropdownRender={menu => (
                            <div>
                              {menu}
                              {canAdd && <>
                                <Divider className="my-1"/>
                                <div
                                  className="mx-3 py-1 min-h-6 cursor-pointer text-primary"
                                  onClick={() => onAddRequestModal(index)}>
                                  {t('proposalRequest.form.organizations.administration.addText')}
                                </div>
                              </>}
                            </div>
                          )}>
                          {
                            (isStateMipyme && isInspection ? stateData : data)?.map((opt, i) => (
                              <Option
                                disabled={selectedOptions.find((el) => el?.idNumber === opt?.idNumber)}
                                key={i}
                                value={opt?.idNumber}
                              >
                                {'(' + opt?.idNumber + ')' + ' ' + getUserFullName(opt)}
                              </Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                      {membersList[index] && membersList[index]?.firstName && <div
                        onClick={() => onEditRequestPartner(membersList[index], index)}
                        className="text-center cursor-pointer flex justify-center hover:text-primary"
                        style={{height: '40px', minWidth: '30px'}}>
                        <EditOutlined/>
                      </div>}
                      {<div
                        onClick={() => onRemoveAndUpdate(index, remove)}
                        className="text-center cursor-pointer flex justify-center hover:text-primary"
                        style={{height: '40px', minWidth: '30px'}}>
                        <DeleteOutlined/>
                      </div>}
                    </div>
                  </Input.Group>;
                })}
                <div className="text-sm text-primary cursor-pointer mt-2">
                  <span onClick={() => add(defaultValue)}
                    className="cursor-pointer">{t(actionText)}</span>
                </div>
              </>;
            }}
          </Form.List>
        </div>}
    </>
  );
};


ProposalPartnersAdministration.propTypes = {
  value: PropTypes.any,
  id: PropTypes.string,
  dialogContext: PropTypes.string,
  actionText: PropTypes.string,
  addRepresentativeText: PropTypes.string,
  validations: PropTypes.array,
  canAdd: PropTypes.bool,
  canAddRepresentative: PropTypes.bool,
  defaultValue: PropTypes.object,
  externalData: PropTypes.object,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  isInspection: PropTypes.bool,
};

ProposalPartnersAdministration.defaultProps = {
  value: [],
  canAdd: true,
  actionText: 'elementAdd'
};

export default memo(ProposalPartnersAdministration);
